<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020 (original work) Open Assessment Technologies SA ;
    import { Icon, Button } from '@oat-sa-private/ui-elements';
    import { __, getActualKey, generateElementId } from '@oat-sa-private/ui-core';
    import { createEventDispatcher } from 'svelte';

    const choiceLabels = {
        decimal: i => `${i + 1}`,
        lowerAlpha: i => String.fromCharCode(i + 97),
        upperAlpha: i => String.fromCharCode(i + 65)
    };

    /**
     * @typedef {Object} Image
     * @property {String} src - image src
     * @property {String} alt - image alt text
     * @property {Number} [width] - image width
     * @property {Boolean} [height] - image height
     */

    /**
     * Component to display a selectable choice with checkbox or radio or as an image
     * @property {String} key - choice identifier
     * @property {String} name - `name` of input element
     * @property {Number} [index=0] - choice index in the list
     * @property {String[]} selected - list of selected choices keys
     * @property {String[]} [eliminated=[]] - list of eliminated choices keys
     * @property {String[]} [masked=[]] - list of masked choices keys
     * @property {Image} [image] - choice image
     * @property {Boolean} [showCaption=true] - controls display of the caption container
     * @property {Number} [maxChoices=-1] - display of the control changes to radio if set to 1
     * @property {Boolean} [controls=true] - controld display of choice controls
     * @property {Boolean} [eliminable=false] - controls display of choice elimination button
     * @property {Boolean} [maskable=false] - controls display of choice masked button
     * @property {Boolean} [disabled=false] - controls disabled state of the choice
     * @property {String} [choiceLabel] - display a label in front of the choice `decimal` | `lowerAlpha` | `upperAlpha`
     * @property {String} [choiceLabelSuffix] - add a suffix to the choice label like `)` or `.`
     * @property {Number} [stacking] - in horizontal mode choices can be stacked in columns
     * @fires 'change'
     */
    export let key;
    export let name;
    export let index = 0;
    export let selected;
    export let eliminated = [];
    export let masked = [];
    export let image = null;
    export let showCaption = true;
    export let maxChoices = -1;
    export let controls = true;
    export let eliminable = false;
    export let maskable = false;
    export let disabled = false;
    export let choiceLabel;
    export let choiceLabelSuffix;
    export let stacking = 0;

    const dispatch = createEventDispatcher();

    // Reference to the input field, in radio button mode.
    // This will be used to retrieve the position inside the group, as well as the group.
    // It will also allow moving the focus to the previous/next sibling radio.
    let radioButton;

    $: radio = maxChoices === 1;

    if (choiceLabel && typeof choiceLabels[choiceLabel] !== 'function') {
        choiceLabel = false;
    }

    const ariaLabelElementId = generateElementId('choice-label');

    $: isDisabled = disabled || eliminated.includes(key) || masked.includes(key) || void 0;

    /**
     * Add this choice to selected choices (or replace them)
     * @param {Event} event
     * @fires change
     */
    function selectChoice(event) {
        if (radio) {
            selected = [key];
        } else {
            selected = [...selected, key];
        }
        dispatch('change', event);
    }

    /**
     * Remove this choice from selected choices
     * @param {Event} event
     * @fires change
     */
    function unselectChoice(event) {
        selected = selected.filter(selectedKey => key !== selectedKey);
        dispatch('change', event);
    }

    /**
     * Toggle eliminated state on this choice
     * @fires change
     */
    function toggleEliminated() {
        if (eliminated.includes(key)) {
            eliminated = eliminated.filter(eliminatedKey => eliminatedKey !== key);
        } else {
            eliminated = [...eliminated, key];
            if (selected.includes(key)) {
                unselectChoice();
                //return right now for change event not to be fired twice
                return;
            }
        }
        dispatch('change');
    }

    /**
     * Toggle maskable state on this choice
     * @fires change
     */
    function toggleMasked() {
        if (masked.includes(key)) {
            masked = masked.filter(maskedKey => maskedKey !== key);
        } else {
            masked = [...masked, key];
            if (selected.includes(key)) {
                unselectChoice();
                //return right now for change event not to be fired twice
                return;
            }
        }
        dispatch('change');
    }

    /**
     * Unselect selected choice, or select unselected choice
     * @param {Event} event
     */
    function toggleChoice(event) {
        if (selected.includes(key)) {
            unselectChoice(event);
        } else {
            selectChoice(event);
        }
    }

    /**
     * List the available radio buttons
     * @returns {Element[]}
     */
    function getRadioButtons() {
        if (!radioButton) {
            return [];
        }

        // the scope of a radio button group is either:
        // - a SelectableChoices, which is the usual container with respect to the LDS
        // - the whole page (less usual, but still possible)
        // Note: different groups with the same name will collide as the selection will jump
        // between them. However, this should not affect the navigation per focus, unless a group
        // is defined outside of a SelectableChoices.
        const scope = radioButton.closest('.selectable-choice-container') || document.body;

        // we need to list all radio buttons of the group
        return Array.from(scope.querySelectorAll(`input[type=radio][name="${name}"]:not(:disabled)`));
    }

    /**
     * Move the focus to the following radio button in the group with respect to the direction.
     * @param {Number} direction - The step and direction to move the focus (i.e 1 or -1)
     */
    function setFocusedRadioButton(direction) {
        const radioButtons = getRadioButtons();

        if (!radioButtons.length) {
            return;
        }

        // each single instance of the component can manage the group,
        // but only the focused one can receive the keyboard event and can trigger the action,
        // so that the current instance is the focused element as it received the event.
        const focusedIndex = radioButtons.findIndex(el => el === radioButton);

        // the next radio button to focus is cycled over the list, hence the formula below
        const nextIndex = (focusedIndex + direction + radioButtons.length) % radioButtons.length;
        radioButtons[nextIndex].focus();
    }

    /**
     * Move the focus to the first or to the last radio button of the group with respect to the direction.
     * @param {Number} direction - The direction to move the focus (i.e 1 or -1)
     */
    function focusBorderRadioButton(direction) {
        const radioButtons = getRadioButtons();
        const borderRadio = direction < 0 ? radioButtons[0] : radioButtons.slice(-1)[0];
        if (borderRadio && borderRadio !== radioButton) {
            borderRadio.focus();
        }
    }

    /**
     * Handle keydown event on the choice input element
     * @param {KeyboardEvent} e
     */
    function handleKeyDown(e) {
        if (!radio) {
            return;
        }

        const keyboardKey = getActualKey(e);
        switch (keyboardKey) {
            case 'down':
            case 'right':
                e.preventDefault();
                e.stopPropagation();
                setFocusedRadioButton(1);
                break;

            case 'up':
            case 'left':
                e.preventDefault();
                e.stopPropagation();
                setFocusedRadioButton(-1);
                break;

            case 'tab':
                // move the focus to the border radio button with respect to the Tab direction
                // this will allow escaping from the radio group without cycling on each element.
                // Known issue: it may introduce a flickering as the border element will get the focus for a few ms.
                // todo: find a better way to address the Tab browsing issue when no radio is selected.
                focusBorderRadioButton(e.shiftKey ? -1 : 1);
                break;
        }
    }

    /**
     * Handle keyup event on the choice input element
     * @param {KeyboardEvent} event
     */
    function handleKeyUp(event) {
        const keyboardKey = getActualKey(event);
        switch (keyboardKey) {
            case 'enter':
            case 'space':
                // space must not activate input directly
                event.preventDefault();
                event.stopPropagation();

                toggleChoice(event);
                break;
        }
    }

    /**
     * Uncheck checked checkbox, or check unchecked checkbox
     * @param {Event} event
     */
    function toggleCheckbox(event) {
        // checked state already reflects the future result of the event on the input
        if (event.target.checked) {
            selectChoice(event);
        } else {
            unselectChoice(event);
        }
    }
</script>

<style>
    /* basic style : colors and spacing */
    li {
        --stacking: 0;
        --choice-margin: var(--space-1x);
        --choice-height: 6rem;
        --action-width: 6rem;
        --radius-fixed-medium: calc(var(--radius-medium) + 1px); /* 1px is a fix of render issue */

        margin: var(--choice-margin);
        padding: 0;
        position: relative;

        /* Voice over bug fix https://developer.mozilla.org/en-US/docs/Web/CSS/list-style-type#Accessibility_concerns */
        &:before {
            content: '\200B';
            position: absolute;
        }
    }
    .stacking {
        flex-grow: 0;
        flex-shrink: 0;
        flex-basis: calc((100% / var(--stacking)) - (var(--choice-margin) * 2));
    }

    .action-container {
        position: absolute;
        color: var(--color-text-default);
        top: 0;
        inset-inline-end: 0;
        bottom: 0;
        text-align: center;
        border-start-start-radius: 0;
        border-start-end-radius: var(--radius-large);
        border-end-end-radius: var(--radius-large);
        border-end-start-radius: 0;
        border: solid var(--border-medium) var(--color-border-default);
        display: flex;

        &.disabled {
            border-color: var(--color-border-disabled);
        }

        & :global-nested(button.small) {
            border: none;
            margin: 0;
            padding: 0;
            width: 100%;
            height: 100%;
            min-width: var(--action-width);
            border-radius: 0;

            &:last-child {
                border-start-start-radius: 0;
                border-start-end-radius: var(--radius-large);
                border-end-end-radius: var(--radius-large);
                border-end-start-radius: 0;
            }

            &:after {
                z-index: 1;
            }

            & svg {
                margin: 0 !important;
            }
        }
    }
    label {
        margin: 0;
        padding: 0;
        position: relative;
        display: block;
        width: 100%;
        height: 100%;
        margin-inline-end: var(--action-width);
    }

    .container {
        @add-mixin selectable;
        display: flex;
        position: relative;
        width: 100%;
        height: 100%;
        min-height: var(--choice-height);
        border-radius: var(--radius-large);
        font-size: var(--fontsize-body);
    }

    .caption-container {
        flex: 1 0 var(--choice-height);
        display: flex;
        flex-flow: row nowrap;
        position: relative;
        padding: calc(var(--space-1x5) - var(--border-medium));
        border-radius: var(--radius-fixed-medium);

        & .choice-label {
            min-width: 4rem;
        }
        & .choice-content {
            flex: 1 1 auto;
        }
    }

    .has-image {
        & .container {
            flex-direction: column;
            &:before {
                position: absolute;
                top: calc(-1 * var(--border-medium));
                left: calc(-1 * var(--border-medium));
                bottom: calc(-1 * var(--border-medium));
                right: calc(-1 * var(--border-medium));
                border-style: solid;
                border-radius: var(--radius-large);
                box-shadow: inset 0 0 0 var(--border-medium) var(--color-bg-default);
                z-index: 1;
                pointer-events: none;
            }
        }

        & .caption-container {
            flex: 0 0 var(--choice-height);
            border-top: var(--border-medium) solid var(--color-border-default);
            border-radius: 0 0 var(--radius-fixed-medium) var(--radius-fixed-medium);
        }

        & .action-container {
            border-inline-start-width: 0;
        }
    }

    .has-image.no-caption input:checked ~ .container:before {
        content: '';
        border-color: var(--color-border-selected);
        border-width: var(--border-fat);
    }

    .has-actions {
        & .container {
            width: calc(100% - var(--action-width) + var(--border-medium));
            border-inline-end-width: 0;

            &,
            &:before {
                border-start-end-radius: 0;
                border-end-end-radius: 0;
            }
        }

        & .caption-container {
            border-radius: var(--radius-fixed-medium) 0 0 var(--radius-fixed-medium);
        }
    }

    .has-actions.has-image .caption-container {
        border-radius: 0 0 0 var(--radius-fixed-medium);
    }

    .image-container {
        position: relative;
        flex: 1 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        border-radius: var(--radius-fixed-medium) var(--radius-fixed-medium) 0 0;
        & img {
            margin: auto;
            display: block;
            transition: all 0.2s ease-in-out;
        }
    }
    .no-caption .image-container {
        border-radius: var(--radius-fixed-medium);
    }

    .has-actions .image-container {
        border-start-start-radius: var(--radius-fixed-medium);
        border-start-end-radius: 0;
        border-end-end-radius: 0;
        border-end-start-radius: 0;
    }

    .has-actions.no-caption .image-container {
        border-start-start-radius: var(--radius-fixed-medium);
        border-start-end-radius: 0;
        border-end-end-radius: 0;
        border-end-start-radius: var(--radius-fixed-medium);
    }

    .has-image.has-actions .container {
        border-inline-end-width: var(--border-medium);
        width: calc(100% - var(--action-width));
    }

    /* container hover style */
    li:not(.eliminated) label:hover input:not(:disabled) ~ .container,
    label:hover input:checked:not(:disabled) ~ .container,
    li:not(.eliminated) input:focus-visible:not(:disabled) ~ .container {
        &,
        & .caption-container {
            @add-mixin selectable-hovered;

            &:before {
                border-color: var(--color-border-active);
            }
        }
        background: none;
    }

    .has-image.no-caption:not(.eliminated) {
        & label:hover input:not(:disabled) ~ .container,
        & input:focus-visible:not(:disabled) ~ .container {
            &:before {
                content: '';
                border-width: var(--border-thick);
            }
            & .image-container img {
                transform: scale(1.1);
            }
        }

        & label:hover input:checked:not(:disabled) ~ .container:before,
        & input:checked:focus-visible:not(:disabled) ~ .container:before {
            border-width: var(--border-fat);
        }
    }
    /* TODO:    change to :global(:focus-visible) ~ .container
                after https://github.com/sveltejs/svelte/issues/5499 is fixed in svelte
    */
    input:focus-visible ~ .container {
        @add-mixin outline-focus-after -1rem;

        &:after {
            border-radius: var(--radius-large);
            z-index: 1;
        }
    }

    /* disabled state */
    input:disabled ~ .container,
    label:hover input:disabled ~ .container,
    .eliminated .container {
        @add-mixin selectable-disabled;

        & .image-container img {
            transition: none;
        }

        & .caption-container {
            &,
            &:before {
                border-color: var(--color-border-disabled);
            }
        }
    }

    input {
        opacity: 0;
        position: absolute;
    }

    input:checked:not(:disabled) ~ .container {
        @add-mixin selectable-selected;
        background: none;

        & .caption-container {
            @add-mixin selectable-selected;
            &:before {
                border-color: var(--color-bg-selected);
            }
        }
    }

    .controls .caption-container {
        padding-inline-start: var(--space-5x);

        /* The empty checkbox */
        &:before {
            content: '';
            display: block;
            position: absolute;
            border: solid var(--border-medium) var(--color-border-default);
            border-radius: var(--radius-medium);
            background-color: var(--color-bg-default);
            width: 2rem;
            height: 2rem;
            inset-inline-start: 2rem;
            top: calc(50% - 1rem);
        }
    }

    .controls:not(.radio) {
        /* The checkbox check (when hidden) */
        & .caption-container > :global(svg) {
            display: none;
            position: absolute;
            inset-inline-start: 2rem;
            top: calc(50% - 0.75rem);
            width: 1.5rem;
            height: 1.5rem;
            z-index: 10;
            color: var(--color-bg-selected);
        }

        & input:checked ~ .container .caption-container > :global(svg) {
            display: block;
        }
        & input:disabled ~ .container .caption-container > :global(svg) {
            color: var(--color-text-disabled);
        }
    }
    .controls.radio {
        /* The empty radio button */
        & .caption-container:before {
            border-radius: var(--radius-circular);
        }
        /* The small circle inside a selected radio */
        & input:checked ~ .container .caption-container:after {
            content: '';
            display: block;
            position: absolute;
            background-color: var(--color-bg-selected);
            border: solid var(--border-thin) var(--color-bg-default);
            border-radius: var(--radius-circular);
            width: 1rem;
            height: 1rem;
            inset-inline-start: 2.5rem;
            top: calc(50% - 0.5rem);
        }
        & input:disabled ~ .container .caption-container:after {
            border-color: var(--color-border-disabled);
            background-color: var(--color-bg-disabled);
        }
    }

    .eliminated {
        & label {
            pointer-events: none;
        }

        & .caption-container:before,
        & .caption-container:after,
        & .caption-container > :global(svg),
        & .choice-label {
            visibility: hidden;
        }

        & .choice-content {
            text-decoration: line-through;
        }

        & .image-container:before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
            background: var(--color-bg-default);
            opacity: 0.75;
            border-start-start-radius: var(--radius-fixed-medium);
            border-start-end-radius: 0;
            border-end-end-radius: 0;
            border-end-start-radius: 0;
        }

        &.no-caption .image-container:before {
            border-start-start-radius: var(--radius-fixed-medium);
            border-start-end-radius: 0;
            border-end-end-radius: 0;
            border-end-start-radius: var(--radius-fixed-medium);
        }

        & .image-container:after {
            content: '';
            position: absolute;
            top: 2rem;
            left: 2rem;
            bottom: 2rem;
            right: 2rem;
            /* puts a cross to the image */
            background: linear-gradient(
                    to top left,
                    rgba(0, 0, 0, 0) 0%,
                    rgba(0, 0, 0, 0) calc(50% - 0.25rem),
                    rgba(0, 0, 0, 1) 50%,
                    rgba(0, 0, 0, 0) calc(50% + 0.25rem),
                    rgba(0, 0, 0, 0) 100%
                ),
                linear-gradient(
                    to top right,
                    rgba(0, 0, 0, 0) 0%,
                    rgba(0, 0, 0, 0) calc(50% - 0.25rem),
                    rgba(0, 0, 0, 1) 50%,
                    rgba(0, 0, 0, 0) calc(50% + 0.25rem),
                    rgba(0, 0, 0, 0) 100%
                );
        }
    }

    .masked {
        & label {
            pointer-events: none;

            & .container {
                background-color: rgba(0, 0, 0, 1);
                color: rgba(0, 0, 0, 1);
            }
        }

        & .caption-container:before,
        & .caption-container:after,
        & .caption-container > :global(svg),
        & .choice-label {
            visibility: hidden;
        }
        & .image-container {
            visibility: hidden;
        }
    }
</style>

<!--Need to duplicate the content for screenreaders as because they do not always read it properly inside label (i.e. math expressions)-->
<span class="hidden" id={ariaLabelElementId}>
    {#if image}
        <span class="image-container">
            <img src={image.src} alt={image.alt || ''} width={image.width} height={image.height} />
        </span>
    {/if}
    {#if showCaption}
        <span class="caption-container">
            {#if choiceLabel}
                <span class="choice-label">{choiceLabels[choiceLabel](index)}{choiceLabelSuffix}</span>
            {/if}
            <span class="choice-content">
                <slot />
            </span>
        </span>
    {/if}
</span>

<li
    class:eliminated={eliminated.includes(key)}
    class:masked={masked.includes(key)}
    class:stacking
    class:has-image={image}
    class:no-caption={!showCaption}
    class:has-actions={eliminable || maskable}
    class:controls
    class:radio
    on:keypress
    on:click
    style="--stacking:{Math.max(stacking, 0)}">
    <!-- svelte-ignore a11y-label-has-associated-control -->
    <label>
        {#if radio}
            <input
                class:focus-visible={false}
                type="radio"
                {name}
                value={key}
                checked={selected.includes(key)}
                disabled={isDisabled}
                bind:this={radioButton}
                on:keydown={handleKeyDown}
                on:keyup={handleKeyUp}
                on:click={toggleChoice}
                aria-labelledby={ariaLabelElementId} />
        {:else}
            <input
                type="checkbox"
                {name}
                value={key}
                checked={selected.includes(key)}
                disabled={isDisabled}
                on:keyup={handleKeyUp}
                on:click={toggleCheckbox}
                aria-labelledby={ariaLabelElementId} />
        {/if}
        <span class="container">
            {#if image}
                <span class="image-container">
                    <img src={image.src} alt={image.alt || ''} width={image.width} height={image.height} />
                </span>
            {/if}
            {#if showCaption}
                <span class="caption-container">
                    {#if controls && !radio}
                        <Icon name="checkbox-check-16" ariaHidden={true} />
                    {/if}
                    {#if choiceLabel}
                        <span class="choice-label">{choiceLabels[choiceLabel](index)}{choiceLabelSuffix}</span>
                    {/if}
                    <span class="choice-content">
                        <slot />
                    </span>
                </span>
            {/if}

            {#if eliminable && eliminated.includes(key)}<span class="visually-hidden">{__('eliminated')}</span>{/if}

            {#if maskable && masked.includes(key)}<span class="visually-hidden">{__('masked')}</span>{/if}
        </span>
    </label>
    {#if eliminable || maskable}
        <div class="action-container" class:disabled on:keydown|stopPropagation>
            {#if eliminable}
                <Button
                    skin="secondary"
                    size="small"
                    label=""
                    ariaLabel={eliminated.includes(key)
                        ? __('restore choice number %d', index + 1)
                        : __('eliminate choice number %d', index + 1)}
                    {disabled}
                    icon={eliminated.includes(key) ? 'revert-16' : image ? 'remove-16' : 'eliminate-16'}
                    on:click={toggleEliminated} />
            {/if}
            {#if maskable}
                <Button
                    skin="secondary"
                    size="small"
                    label=""
                    ariaLabel={masked.includes(key)
                        ? __('unmask choice number %d', index + 1)
                        : __('mask choice number %d', index + 1)}
                    {disabled}
                    icon={masked.includes(key) ? 'eye-16' : 'eye-crossed-16'}
                    on:click={toggleMasked} />
            {/if}
        </div>
    {/if}
</li>
